/* eslint-disable max-len */
import { JSONSchema7 } from 'json-schema';
import { types as commonTypes, customSchemas } from '@sitebuilder/common';
import DropDownWidget from './DropDown';

export const PLACEHOLDER = 'Use placeholder instead';
export const FIRSTOPTION = 'Use first drop down option';
export const CURRENTOPTION = 'Use current option';

const schema: JSONSchema7 = {
    title: 'Drop Down',
    type: 'object',
    required: ['primaryDataKey', 'primaryDisplayName', 'primaryRedirectRef'],
    description: 'This widget support drop down list with redirect',
    properties: {
        styles: {
            type: 'string',
            description: 'A stylesheet',
            default: '',
        },
        ...customSchemas.textSchema.TextSchema.schema,
        ...customSchemas.styleSchema.StyleSchema.schema,
        ...customSchemas.layoutSchema.LayoutSchema.schema,
        targetTop: {
            type: 'boolean',
            title: 'Navigate top window',
            description: 'If within iframe navigate parent window or not',
            default: false,
        },
        primaryDataKey: {
            type: 'string',
            title: 'Data key',
            description: 'Choose a key that resolve to an array of object',
            default: '',
        },
        primaryDisplayName: {
            type: 'string',
            title: 'Drop down item display names ',
            description:
                'Specify an object property relative to data key, Enter comma separated values for multiple names',
            default: '',
        },
        multipleNameSeparator: {
            type: 'string',
            title: 'Sperate display name with',
            default: ' - ',
        },
        primaryRedirectRef: {
            type: 'string',
            title: 'Reference to redirect',
            description: 'Specify an object property relative to data key',
            default: '',
        },
        primaryLabel: {
            type: 'string',
            title: 'Primary label',
            default: '',
        },
        customDropOptionLength: {
            type: 'integer',
            title: 'Specify the width of the dropdown options (optional)',
            description: 'Use this when option dropdown need more width',
        },
        showSearch: {
            type: 'boolean',
            title: 'Show search',
            description: 'Selections will have a search field',
            default: false,
        },
        defaultOption: {
            type: 'string',
            title: 'Default option ',
            enum: [PLACEHOLDER, FIRSTOPTION, CURRENTOPTION],
            description: 'Use placeholder or use first dropdown item by default',
            default: FIRSTOPTION,
        },
        supportSecondaryData: {
            type: 'boolean',
            default: false,
            title: 'Add secondary drop down',
        },
        transformData: {
            type: 'boolean',
            title: 'Transform Data',
            default: false,
        },
        primaryAriaLabel: {
            type: 'string',
            title: 'Primary drop down aria label',
        },
        secondaryAriaLabel: {
            type: 'string',
            title: 'Secondary drop down aria label',
        },
    },
    dependencies: {
        targetTop: {
            oneOf: [
                {
                    properties: {
                        targetTop: { enum: [true] },
                        topURLDataKey: {
                            type: 'string',
                            title: 'Top window URL Data key',
                        },
                        topURLDataOption: {
                            type: 'boolean',
                            title: 'Select top url from dropdown option',
                        },
                    },
                    required: ['topURLDataKey'],
                },
                {
                    properties: {
                        targetTop: { enum: [false] },
                    },
                },
            ],
        },
        supportSecondaryData: {
            oneOf: [
                {
                    properties: {
                        supportSecondaryData: { enum: [true] },
                        secondaryDisplayName: {
                            type: 'string',
                            title: 'Secondary drop down item display name ',
                            description:
                                'Specify an object property relative to data key, Enter comma separated values for multiple names',
                            default: '',
                        },
                        multipleSecNameSeparator: {
                            type: 'string',
                            title: 'Sperate display name with',
                            default: ' - ',
                        },
                        secRedirectRef: {
                            type: 'string',
                            title: 'Reference to redirect',
                            description: 'Specify an object property relative to data key',
                            default: '',
                        },
                        secLabel: {
                            type: 'string',
                            title: 'Secondary Label',
                            default: '',
                        },
                        secDefaultOption: {
                            type: 'string',
                            title: 'Default option for secondary dropdown',
                            enum: [PLACEHOLDER, FIRSTOPTION, CURRENTOPTION],
                            default: FIRSTOPTION,
                        },
                    },
                    required: ['secondaryDisplayName', 'secRedirectRef'],
                    dependencies: {
                        secDefaultOption: {
                            oneOf: [
                                {
                                    properties: {
                                        secDefaultOption: { enum: [PLACEHOLDER] },
                                        secPlaceholder: {
                                            type: 'string',
                                            title: 'Secondary PlaceHolder',
                                            default: '',
                                        },
                                    },
                                },
                                {
                                    properties: {
                                        secDefaultOption: { enum: [FIRSTOPTION] },
                                    },
                                },
                                {
                                    properties: {
                                        secDefaultOption: { enum: [CURRENTOPTION] },
                                        secCurrentItemDatakey: {
                                            type: 'string',
                                            title: 'Reference to current item for secondary dropdown',
                                            description:
                                                'Specify an object property relative to data key',
                                            default: '',
                                        },
                                    },
                                },
                            ],
                        },
                    },
                },
            ],
        },
        defaultOption: {
            oneOf: [
                {
                    properties: {
                        defaultOption: { enum: [PLACEHOLDER] },
                        placeholder: {
                            type: 'string',
                            description: 'Primary placeholder',
                        },
                    },
                },
                {
                    properties: {
                        defaultOption: { enum: [FIRSTOPTION] },
                    },
                },
                {
                    properties: {
                        defaultOption: { enum: [CURRENTOPTION] },
                        currentItemDatakey: {
                            type: 'string',
                            title: 'Reference to current item',
                            description: 'Specify an object property relative to data key',
                            default: '',
                        },
                    },
                },
            ],
        },
        transformData: {
            oneOf: [
                {
                    properties: {
                        transformData: { enum: [true] },
                        functionToTransformData: {
                            type: 'string',
                            description: 'Function to transform Data',
                            default: 'return (data) => { return data}',
                        },
                    },
                },
            ],
        },
        ...customSchemas.layoutSchema.LayoutSchema.dependencies,
    },
};

const groups = [
    {
        'Style Editor': ['styles'],
        ...customSchemas.textSchema.TextSchema.group,
        ...customSchemas.styleSchema.StyleSchema.group,
        ...customSchemas.layoutSchema.LayoutSchema.group,
        Values: [
            'targetTop',
            'topURLDataKey',
            'topURLDataOption',
            'primaryDataKey',
            'primaryDisplayName',
            'multipleNameSeparator',
            'primaryRedirectRef',
            'primaryLabel',
            'customDropOptionLength',
            'showSearch',
            'defaultOption',
            'placeholder',
            'currentItemDatakey',
            'supportSecondaryData',
            'secondaryDisplayName',
            'multipleSecNameSeparator',
            'secRedirectRef',
            'secLabel',
            'secDefaultOption',
            'primaryAriaLabel',
            'secondaryAriaLabel',
            'secPlaceholder',
            'secCurrentItemDatakey',
        ],
        'Transform Data': ['transformData', 'functionToTransformData'],
    },
];

const DropDown: commonTypes.IWidget = {
    schema,
    uiSchema: {
        'ui:groups': groups,
        ...customSchemas.textSchema.TextSchema.uiSchema,
        ...customSchemas.styleSchema.StyleSchema.uiSchema,
        ...customSchemas.layoutSchema.LayoutSchema.uiSchema,
        styles: {
            'ui:widget': 'CSSEditor',
        },
        functionToTransformData: { 'ui:widget': 'JSEditor' },
    },
    group: 'component',
    templateView: DropDownWidget,
    id: 'DropDown',
    contentPlaceHolders: ['children'],
    initialData: () => ({}),
};

export default DropDown;
